import React, { createContext, useContext, useEffect, useState } from "react";

export interface TabContextType {
  activeTab: string;
  setActiveTab: React.Dispatch<React.SetStateAction<string>>;
  changeTab: (tab: string) => void;
}

const TabContext = createContext<TabContextType | undefined>(undefined);

export const useTabContext = () => {
  const context = useContext(TabContext);
  if (context === undefined) {
    throw new Error("useTabContext must be used within a TabContextProvider");
  }
  return context;
};

interface TabContextProviderProps {
  children: React.ReactNode;
  defaultActiveKey: string; // Define the defaultActiveKey prop
  activeTabKey?: string; // Define the defaultActiveKey prop
  onTabChange?: (activeTab: string) => void;
}

export const TabContextProvider: React.FC<TabContextProviderProps> = ({
  children,
  defaultActiveKey,
  activeTabKey,
  onTabChange,
}) => {
  const [activeTab, setActiveTab] = useState<string>(defaultActiveKey);

  const changeTab = (newTab: string) => {
    setActiveTab(newTab);
  };

  useEffect(() => {
    if (onTabChange) onTabChange(activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTabKey) setActiveTab(activeTabKey);
  }, [activeTabKey]);

  return <TabContext.Provider value={{ activeTab, setActiveTab, changeTab }}>{children}</TabContext.Provider>;
};
